import type FullCalendar from '@fullcalendar/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { usePermissions, usePreference } from '@tyro/api';
import {
  ActionMenu,
  type MenuItemConfig,
  toast,
  useDisclosure,
  useResponsive,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import {
  AddIcon,
  CalendarCheckmarkIcon,
  CalendarLinkIcon,
  CalendarWithXIcon,
  EditCalendarIcon,
  EyeIcon,
  EyeSlashIcon,
  PrinterIcon,
} from '@tyro/icons';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useMemo,
} from 'react';
import type { CalendarView } from '../../../../@types/calendar';
import {
  useEmbeddableCalendarConfig,
  useUpdateEmbeddableCalendarConfig,
} from '../../../../api/embedable-cal';
import { DateSwitcher } from './date-switcher';
import { EmbeddableCalendarModal } from './embeddable-calendar-modal';
import { CalendarViewSwitcher } from './view-switcher';

dayjs.extend(LocalizedFormat);

const RootStyle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  padding: theme.spacing(2.5),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));

interface CalendarToolbarProps {
  calendarRef: RefObject<FullCalendar | null>;
  date: Date;
  setDate: Dispatch<SetStateAction<Date>>;
  view: CalendarView;
  onEditCalendar: VoidFunction;
  onAddEvent: VoidFunction;
  onChangeView: (newView: CalendarView) => void;
  hasMultipleResources?: boolean;
  canAddEvent?: boolean;
  onPrint: VoidFunction;
  showEmbeddableCalendarActions?: boolean;
}

export function CalendarToolbar({
  calendarRef,
  date,
  setDate,
  view,
  onEditCalendar,
  onAddEvent,
  onChangeView,
  hasMultipleResources = true,
  canAddEvent,
  onPrint,
  showEmbeddableCalendarActions = false,
}: CalendarToolbarProps) {
  const isDesktop = useResponsive('up', 'sm');
  const { t } = useTranslation(['common', 'calendar']);
  const {
    isOpen: isEmbedCodeOpen,
    onOpen: onEmbedCodeOpen,
    onClose: onEmbedCodeClose,
  } = useDisclosure();
  const [showWeekends, setShowWeekends] = usePreference(
    'calendar.show-weekends',
    false,
  );
  const { isStaffUser } = usePermissions();
  const { data: embeddableCalendarConfig } = useEmbeddableCalendarConfig({
    enabled: showEmbeddableCalendarActions,
  });
  const { mutateAsync: updateEmbeddableCalendarConfig } =
    useUpdateEmbeddableCalendarConfig();

  const embeddableCalendarActions = useMemo<MenuItemConfig[]>(() => {
    if (!showEmbeddableCalendarActions || !embeddableCalendarConfig) return [];

    if (!embeddableCalendarConfig.enabled) {
      return [
        {
          label: t('calendar:enableEmbeddableCalendar'),
          icon: <CalendarCheckmarkIcon />,
          onClick: async () => {
            toast.promise(
              async () => {
                await updateEmbeddableCalendarConfig({
                  enable: true,
                });
                onEmbedCodeOpen();
              },
              {
                loading: t('common:loading'),
              },
            );
          },
        },
      ];
    }

    return [
      {
        label: t('calendar:disableEmbeddableCalendar'),
        icon: <CalendarWithXIcon />,
        onClick: () => {
          updateEmbeddableCalendarConfig({
            enable: false,
          });
        },
      },
      {
        label: t('calendar:viewEmbedCode'),
        icon: <CalendarLinkIcon />,
        onClick: onEmbedCodeOpen,
      },
    ];
  }, [
    showEmbeddableCalendarActions,
    embeddableCalendarConfig,
    updateEmbeddableCalendarConfig,
    onEmbedCodeOpen,
  ]);

  const currentDate = dayjs(date);
  const onPreviousDateClick = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const onNextDateClick = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const onChangeDate = (newDate: dayjs.Dayjs) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.gotoDate(newDate.toDate());
      setDate(calendarApi.getDate());
    }
  };

  return (
    <>
      <RootStyle className="toolbar">
        {isDesktop && (
          <CalendarViewSwitcher
            value={view}
            onChange={onChangeView}
            hasMultipleResources={hasMultipleResources}
          />
        )}

        <DateSwitcher
          onPreviousDateClick={onPreviousDateClick}
          onNextDateClick={onNextDateClick}
          date={currentDate}
          onChangeDate={onChangeDate}
        />

        <Stack direction="row" spacing={2}>
          <ActionMenu
            buttonProps={{
              size: 'small',
              color: 'primary',
              variant: 'text',
              sx: {
                '& .MuiButton-endIcon': {
                  ml: 0.5,
                },
              },
            }}
            menuItems={[
              [
                {
                  label: t('calendar:addEvent'),
                  onClick: onAddEvent,
                  icon: <AddIcon />,
                  hasAccess: () => !!canAddEvent,
                },
                {
                  label: showWeekends
                    ? t('calendar:hideWeekends')
                    : t('calendar:showWeekends'),
                  onClick: () => setShowWeekends(!showWeekends),
                  icon: showWeekends ? <EyeSlashIcon /> : <EyeIcon />,
                },
                {
                  label: t('common:actions.print'),
                  onClick: onPrint,
                  icon: <PrinterIcon />,
                },
              ],
              embeddableCalendarActions,
            ]}
          />
          {isStaffUser && (
            <Button
              size="small"
              color="primary"
              variant="text"
              onClick={onEditCalendar}
              startIcon={<EditCalendarIcon sx={{ width: 20, height: 20 }} />}
            >
              {t('calendar:filterCalendar')}
            </Button>
          )}
        </Stack>
      </RootStyle>
      <EmbeddableCalendarModal
        isOpen={isEmbedCodeOpen}
        onClose={onEmbedCodeClose}
      />
    </>
  );
}
